import { Typography } from 'src/components/common/Typography'
import React, { useEffect, useRef, useState } from 'react'
import { Container } from 'src/components/common/Container'
import styled, { css } from 'styled-components'
import { MainPageProps } from 'src/pages/MainPage'
import {
  Noto_body2_r14,
  Noto_body3_m12,
  Noto_desc_b10,
  Noto_heading3_b20,
} from 'src/styles/fonts/NotoSansKrFonts'
import { ellip, mediaMaxWidth } from 'src/styles/mixin'
import useResizeEventListener from 'src/hooks/useResizeEventListener.hook'
import LazyImage from 'src/components/common/LazyImage'
import usePageNav from 'src/hooks/usePageNav.hook'
import { CustomSwiper } from 'src/components/common/CustomSwiper'
import Skeleton from 'react-loading-skeleton'
import RenderIf from 'src/components/common/RenderIf'

const MainRecommendBrand = ({ recommendBrandList }: Pick<MainPageProps, 'recommendBrandList'>) => {
  const { brandSalePageNav } = usePageNav()
  const containerRef = useRef<HTMLDivElement>(null)
  const [left, setLeft] = useState<string>('0px')

  const calculateLeftPosition = () =>
    setLeft(`${(containerRef?.current?.clientWidth! - 420) / 2}px`)

  useEffect(() => {
    const rafId = requestAnimationFrame(calculateLeftPosition)
    return () => {
      window.cancelAnimationFrame(rafId)
    }
  }, [])

  useResizeEventListener(calculateLeftPosition)
  return (
    <S.MainRecommendBrandContainer $option="flex_column" ref={containerRef} $row_gap="30px">
      {recommendBrandList?.map(({ Title, Contents, Brands }, r_i: number) => (
        <S.MainRecommendBrand.Container $option="flex_column" key={r_i} $left={left}>
          <S.MainRecommendBrand.Title
            $fontStyle={Noto_heading3_b20}
            $color="var(--black-90)"
            as={!!Title ? Typography : Skeleton}
          >
            {Title}
          </S.MainRecommendBrand.Title>
          <S.MainRecommendBrand.Desc
            $fontStyle={Noto_body2_r14}
            $color="var(--black-50)"
            as={!!Contents ? Typography : Skeleton}
          >
            {Contents}
          </S.MainRecommendBrand.Desc>
          <CustomSwiper.Wrap
            type="default"
            $wrapstyle={S.MainRecommendBrand.BrandList.Container}
            swiperOption={{
              autoplay: { delay: 800, disableOnInteraction: false },
              loop: true,
            }}
          >
            {Brands?.map(
              (
                { BrandImage, BrandName, BrandNumber, HighestDiscountRate, CategoryNumber },
                b_i: number,
              ) => (
                <CustomSwiper.Item
                  key={b_i}
                  $itemstyle={S.MainRecommendBrand.BrandItems.Container}
                  onClick={() => brandSalePageNav(CategoryNumber, BrandNumber, BrandName)}
                >
                  <S.MainRecommendBrand.BrandItems.AspectRatioImage>
                    <LazyImage
                      src={BrandImage}
                      alt="브랜드이미지"
                      style={S.MainRecommendBrand.BrandItems.CouponImage}
                    />
                  </S.MainRecommendBrand.BrandItems.AspectRatioImage>
                  <RenderIf when={!!BrandName}>
                    <S.MainRecommendBrand.BrandItems.BrandName>
                      {BrandName}
                    </S.MainRecommendBrand.BrandItems.BrandName>
                    <S.MainRecommendBrand.BrandItems.DiscountRate
                      as={!!HighestDiscountRate ? Typography : Skeleton}
                    >
                      {Math.floor((HighestDiscountRate as number) * 10) / 10}% 할인
                    </S.MainRecommendBrand.BrandItems.DiscountRate>
                  </RenderIf>
                  <RenderIf when={!BrandName}>
                    <Skeleton count={2} style={{ marginBottom: '2px' }}></Skeleton>
                  </RenderIf>
                </CustomSwiper.Item>
              ),
            )}
          </CustomSwiper.Wrap>
        </S.MainRecommendBrand.Container>
      ))}
    </S.MainRecommendBrandContainer>
  )
}
export default MainRecommendBrand

const S = {
  MainRecommendBrandContainer: styled(Container)`
    background: var(--white);
    padding: 20px 0;
    ${mediaMaxWidth('460')} {
      padding: 20px 0;
    }
  `,
  MainRecommendBrand: {
    Container: styled(Container)<{ $left: string }>`
      position: relative;
      width: calc(100% - ${(props) => props.$left});
      left: ${(props) => props.$left};
      ${mediaMaxWidth('460')} {
        left: 0;
        width: 100%;
      }
    `,
    Title: styled(Typography)`
      max-width: 420px;
      margin-bottom: 1px;
      ${mediaMaxWidth('460')} {
        padding: 0 20px;
      }
    `,
    Desc: styled(Typography)`
      max-width: 420px;
      ${mediaMaxWidth('460')} {
        padding: 0 20px;
      }
    `,
    BrandList: {
      Container: css`
        display: flex;
        flex-direction: row;
        margin-top: 14px;
        height: 100%;
        width: 100%;
      `,
    },
    BrandItems: {
      Container: css`
        display: flex;
        flex-direction: column;
        width: 72px;
        text-align: center;
        cursor: pointer;
        margin-right: 20px;
        ${mediaMaxWidth('460')} {
          margin-left: 20px;
          margin-right: 0;
        }
      `,
      AspectRatioImage: styled.section`
        padding-top: 100%;
        display: flex;
        position: relative;
      `,
      CouponImage: css`
        width: 72px;
        height: 72px;
        min-width: 72px;
        min-height: 72px;
        border-radius: 100px;
        border: 1px solid rgba(0, 0, 0, 0.1);
        position: absolute;
        top: 0;
        left: 0;
        margin: 0;
        > img {
          min-width: 72px;
          min-height: 72px;
          object-fit: contain;
          &:hover {
            transform: scale(1.05);
          }
        }
      `,
      BrandName: styled(Typography)`
        margin: 4px auto 4px auto;
        ${ellip('line1')};
        max-width: 72px;
        ${Noto_body3_m12};
        color: var(--black-50);
      `,
      DiscountRate: styled.section`
        ${Noto_desc_b10};
        color: var(--brand-50);
        background-color: var(--brand-20);
        padding: 0 6px;
        width: fit-content;
        border-radius: 50px;
        margin: 0 auto;
      `,
    },
  },
}
